import { ApplicationInsights, DistributedTracingModes, ITelemetryItem } from '@microsoft/applicationinsights-web';
// @ts-ignore TS7016
import dndPageScroll from 'dnd-page-scroll';
import { RouterStateService } from './core/routing/router-state.service';
import { AppVersionService } from './core/services/app-version.service';
import { AuthTokenService } from './core/services/auth-token.service';
import { ConnectivityService } from './core/services/connectivity.service';
import { FullStoryService } from './core/services/fullstory.service';
import { NotificationService } from './core/services/notification.service';
import { PendoFeedbackService } from './core/services/pendo-feedback.service';
import { StoresService } from './core/services/stores';
import { UserEventService } from './core/services/user-event';
import { RepoStartupService } from './data/repositories/repo-startup.service';

export function initializeApp(
    repoStartup: RepoStartupService,
    storesService: StoresService,
    userEventService: UserEventService,
    notificationService: NotificationService,
    connectivityService: ConnectivityService,
    pendoFeedbackService: PendoFeedbackService,
    fullStoryService: FullStoryService,
    appVersionService: AppVersionService,
    authTokenService: AuthTokenService,
    routerStateService: RouterStateService
): () => Promise<void> {
    return () =>
        new Promise((resolve) => {
            // Load initial data for app
            repoStartup.GetAll(true).then(() => {
                storesService.hideSplashScreen();
                // Start required services
                userEventService.watchEventStatusChanges();
                notificationService.start();
                connectivityService.start();
                pendoFeedbackService.start();
                fullStoryService.start();
                routerStateService.start();
                appVersionService.start();
                authTokenService.start();
                resolve();
            });
        });
}

export function initializeThirdParty(): () => void {
    return () => {
        initializeChargeBee();
        initializeAppInsights();
        dndPageScroll({ delay: 50 });
        initializeFullstory();
    };
}

function initializeChargeBee() {
    if (!window['Chargebee']) return;

    const siteName = window.APP_CONFIG.environment !== 'Production' ? 'buildxact-test' : 'buildxact';

    try {
        window['Chargebee'].init({
            site: siteName,
        });
    } catch (err) {
        console.log(err);
    }
}

function initializeAppInsights() {
    window.appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: window.APP_CONFIG.appInsightsInstrumentationKey,
            distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
            enableAutoRouteTracking: true,
            enableCorsCorrelation: true,
            disableFetchTracking: false,
            // @ts-ignore TS2322
            correlationHeaderExcludedDomains: [window.APP_CONFIG.auth0Domain, '*.product.buildxact.com'],
            correlationHeaderDomains: ['*.buildxact.com', '*.buildxact.com.au'],
        },
    });
    window.appInsights.loadAppInsights();
    // send app version on all telemetry
    var telemetryInitializer = (envelope: ITelemetryItem) => {
        if (envelope.baseData) {
            envelope.baseData.properties = envelope.baseData.properties || {};
            envelope.baseData.properties.appVersion = window.APP_CONFIG.appVersion;
        }
    };
    window.appInsights.addTelemetryInitializer(telemetryInitializer);
    window.appInsights.trackPageView();
}

function initializeFullstory() {
    // insert fullstory scriptlet only for prod and staging
    if (
        window.APP_CONFIG.environment?.toLowerCase() === 'production' ||
        window.APP_CONFIG.environment?.toLowerCase() === 'staging' ||
        window.APP_CONFIG.environment?.toLowerCase() === 'preprod'
    ) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'assets/scripts/fullstory.js';
        document.head.appendChild(script);
    }
}

export function initializeRoute(): () => Promise<void> {
    return () =>
        new Promise((resolve, reject) => {
            if (document.location.hash && document.location.hash.startsWith('#/') && document.location.pathname === '/') {
                document.location.href = document.location.href.replace('/#/', '/');
                reject();
            } else resolve();
        });
}
